import { Injectable, Injector } from '@angular/core';
import { Observable, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { ORDER_ACTIONS } from '../store/actions';
import { BundleCall } from '@ispm-client/modules/aggregator/services';
import { isEmpty, get, isArray, has, uniq, sortBy, cloneDeep, unset, isEqual } from 'lodash';
import { AgregationService } from '@ispm-client/modules/aggregator/services/aggregator.service';
import {
  ExportConfig,
  IOrder,
  IOrderDetail,
  IOrderDetailDefinition,
  IOrdersState, ISelectedOrderDefinition, TablelistStateListItemConfig
} from '@ispm-client/modules/orders/interfaces';
import { IEvent, IEventsDefAndSelectedState } from '@ispm-client/modules/events/Interfaces';
import { take, map, filter, distinctUntilChanged, catchError } from 'rxjs/operators';
import { Map as immutableMap } from 'immutable';
import { IspmHttpClient } from '@ispm/core/services/ispm-http-client';
import * as immutable from 'immutable';
import { CoreActionCreatorService } from '@ispm/core/services/core-action-creator.service';
import { CORE_ACTIONS } from '@ispm/core/constants/core-actions.constant';
import { ContextProviderService } from '@ispm-client/modules/context-provider/services/context-provider.service';
import { EventService } from '@ispm-client/modules/events/services/event.service';
import { WorkflowUtilService } from '@ispm-client/modules/aggregator/services/workflow-util.service';
import { HttpInterceptorService } from '@ispm/core/services/http-interceptor.service';
import { currentDiagnosticOrderSelector } from '@ispm/test/store/diagnostic-order.selector';
import { IDiagnosticOrder } from '@ispm/test/interfaces/diagnostic-order.interface';
import { DIAGNOSTIC_ORDER_ACTIONS } from '@ispm/test/constants/diagnostic-order.constants';
import { UpdateResourcesAction } from '@ispm/shared/store/actions/test-bar.actions';
import { ResourceDataItem } from '@ispm/core/classes/resource-data-item.class';
import { IWorkflowRequest } from '@ispm/workflow/interfaces/workflow-request.interface';
import { selectActiveWorkflow } from '@ispm/workflow/store/workflow-config.selectors';
import { BrowserCacheService } from '@ispm/core/services/browser-cache.service';
import { Subject } from 'rxjs/Subject';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { TablelistService } from '../../../app/worklist/services/tablelist.service';
import { TablelistStoreHandlerService } from '../../../app/worklist/services/tablelist-store-handler.service';

@Injectable()
export class OrderService {
  public static ModalLoaderTunnelName = 'ORDER_POPUP_MODAL_CHANNEL';

  private store$: Store<IOrdersState>;
  private eventStore$: Store<IEventsDefAndSelectedState>;
  private lastEventProcessed: IEvent = null;
  private appStore$: Store<any>;
  private noPopupDetails = new BehaviorSubject<any>(null);

  get authService() {
    return this.coreAC.getUpgradeInjectorStatus().pipe(
      filter(status => status),
      take(1),
      map(_ => this.injector.get('AuthenticationService'))
    ).toPromise();
  }

  constructor(
    private store: Store<any>,
    private _bundleCall: BundleCall,
    public _aggregatorService: AgregationService,
    private _ispmHttpClient: IspmHttpClient,
    protected injector: Injector,
    protected coreAC: CoreActionCreatorService,
    private _contextProviderService: ContextProviderService,
    private eventService: EventService,
    private _workflowUtilService: WorkflowUtilService,
    private _isgHttpClient: HttpInterceptorService,
    private _browserCacheService: BrowserCacheService,
    private tablelistService: TablelistService,
    private _tablelistStoreHandlerService: TablelistStoreHandlerService
  ) {
    this.appStore$ = store;
    this.store$ = store.select('orders') as any;
    this.eventStore$ = store.select('events') as any;
  }

  /**
   * GetAndStoreAllOrderDefinitions
   *
   * @description fetches all order definitions and updates it in the store
   *
   * @param testName
   */
  async getAndStoreAllOrderDefinitions(testName: string) {
    this._isgHttpClient.get('test/getTestConfiguration',
      { testTypeId: testName , cacheId: this._browserCacheService.getCacheId()}
    )
      .subscribe(data => {
      this.store$.dispatch({
        type: ORDER_ACTIONS.ADD_ORDER_DETAIL_DEFINITIONS,
        payload: data.body
      });
    });
  }

  /**
   * @returns Observable of selectedEvent
   */
  getEventsData(): Observable<immutableMap<string, IEvent>> {
    return this.eventStore$.select('eventsData');
  }

  getAllEventAndStore(): Promise<any> {
    return this.eventService.getAllEventsAndStore();
  }

  /**
   * @method getAllOrdersAndStore
   * @description Fetches all the orders using dataBinding from the widget configuration
   * @param dataBindings
   * @param excludeTypes
   * @param loader
   */
  async getAllOrdersAndStore(dataBindings: any, excludeTypes = [], loader: boolean = true) {
    if (loader) {
       this.setLoader(true);
    }

    const dataBindingsList = [];
    dataBindings.map(item => dataBindingsList.push({ ...item}));

    for (const db of dataBindingsList) {
      db.queryString = await (this._bundleCall.queryFiller(db.queryString));
    }

    return new Promise(resolve => {
      this._bundleCall.searchCall({ queries: dataBindingsList }).subscribe(async data => {
        const ordersData = this._aggregatorService.responeRestructure(data, dataBindingsList);
        let orders: IOrder[] = get(ordersData, `[0].${get(dataBindingsList, '[0].identifier', 'orders')}`, []);
        let allOrders: immutable.Map<string, IOrder> = immutable.Map();
        if (orders && isArray(orders) && orders.length) {
          orders = orders.filter((o: IOrder) => !excludeTypes.includes(o.category.code) && o.isDeleted !== true);
          await this.getAllOrderDetailsAndStore(orders, loader);
          allOrders = immutable.Map(orders.map((item) => ([item.id, item])));
        }
        this.store$.dispatch({
          type: ORDER_ACTIONS.ADD_ORDERS,
          payload: allOrders
        });
        this.setLoader(false);
        resolve(orders);
      }, () => {
        this.setLoader(false);
      });
    });
  }

  getSingleOrder(order: IOrder): Observable<IOrder> {
    const dataBindings = [
      {
        identifier: 'order',
        queryString: `_id=${order.id}`,
        targetResource: 'OrderInformation',
        identifierType: 'array'
      }
    ];
    return this._bundleCall.searchCall({ queries: dataBindings }).pipe(
      map(data => {
        const ordersData = this._aggregatorService.responeRestructure(data, dataBindings);
        return get(ordersData, `[0].order`).find(o => o.id === order.id);
      })
    );
  }

  /**
   * @method findStatusAndDueDate
   * @param order
   * @param orderDetails
   * @param lastSelectedOrderDetails
   */
  findStatusAndDueDate(order: IOrder, orderDetails: IOrderDetail[], lastSelectedOrderDetails: IOrderDetail[] = []): IOrder {
    let tempOrderDetails: IOrderDetail[];
    if (order.isActive && order.isActive.length) {
      tempOrderDetails = orderDetails.filter( od => order.isActive.includes(od.id) );
    } else {
      tempOrderDetails = orderDetails.filter( od => od.selected === true );
    }
    tempOrderDetails = sortBy( tempOrderDetails, od => new Date( get(od, 'dueDate')) );
    lastSelectedOrderDetails = sortBy(lastSelectedOrderDetails, od => new Date( get(od, 'dueDate')));
    if (tempOrderDetails.length && order?.status?.code !== 'completed' && ((!isEmpty(lastSelectedOrderDetails)
      && (!isEqual(get(lastSelectedOrderDetails, '[0].derivedStatus'), get(tempOrderDetails, '[0].derivedStatus')) || !isEqual(get(lastSelectedOrderDetails, '[0].dueDate'), get(tempOrderDetails, '[0].dueDate'))))
      || isEmpty(lastSelectedOrderDetails) || !isEqual(get(order, 'status'), get(tempOrderDetails, '[0].derivedStatus')))) {

      order.status = get(tempOrderDetails, '[0].derivedStatus');
      order.dueDate = get(tempOrderDetails, '[0].dueDate');
    }
    return order;
  }

  /**
   * Get order information based on current step
   * @param config
   * @param selectedEvent
   */
  checkForOrder(config, selectedEvent): Observable<any> {
    let step = null;
    let stepMatched = [];
    const orderPopupDetails = new BehaviorSubject<any>(null);
    const orderInfoPopupConfig = get(config, 'orderInformationPopup');
    this.appStore$.select('workflowConfig', 'activeWorkflow', 'latestRevisionStatus').subscribe(currentStep => {
      step = currentStep;
    });
    if (!isEmpty(orderInfoPopupConfig)) {
      stepMatched = orderInfoPopupConfig.filter(configStep => get(configStep, 'step').toUpperCase() === step.toUpperCase());
    }
    const currentStep = get(stepMatched, '0');
    if (!isEmpty(get(currentStep, 'dataBinding')) && !isEmpty(get(currentStep, ['dataBinding', 0, 'identifier']))
      && stepMatched.length === 1) {
       this.getAllOrders().pipe(
        take(1)).subscribe(orders => {
        if (!orders.isEmpty()) {
          orderPopupDetails.next(this.checkForOrderExist(orders, selectedEvent, currentStep));
        } else {
          this.getAllOrdersAndStore(get(currentStep, 'dataBinding', []),
            [], false).then((data) => {
            let allOrders: immutable.Map<string, IOrder> = immutable.Map();
            if (data && isArray(data) && data.length) {
              allOrders = immutable.Map(data.map((item) => ([item.id, item])));
            }
            orderPopupDetails.next(this.checkForOrderExist(allOrders, selectedEvent, currentStep));
          });
        }
      });
      return orderPopupDetails.asObservable();
    } else if (isEmpty(stepMatched)) {
      const orderPopupDetails = {
        'orderExist': false,
        'popupDetails': null
      };
      this.noPopupDetails.next(orderPopupDetails);
      return this.noPopupDetails.asObservable();
    }
  }

  /**
   * Check if order category exist
   * @param orders
   * @param selectedEvent
   * @param currentStep
   */
  checkForOrderExist(orders, selectedEvent, currentStep): any {
    let orderPopupDetails;

    if (!orders.isEmpty()) {
      const foundOrder = orders.filter((o: IOrder) => o.category.code === get(selectedEvent, 'category.code'));
      const orderExist = !foundOrder.isEmpty();
      orderPopupDetails = {
        'orderExist': orderExist,
        'popupDetails': currentStep
      };
      return orderPopupDetails;
    } else {
      orderPopupDetails = {
        'orderExist': false,
        'popupDetails': currentStep
      };
      return orderPopupDetails;
    }
  }

  /**
   * @method buildOrderDetailDataBinding
   * @param orderTypes
   * @todo The filtering logic of order details should be driven from the configuration
   */
  buildOrderDetailDataBinding(orderTypes: string[] = []): any[] {
    const currentPatient = this._bundleCall.getPatientContextForInterpolation().currentPatient;
    const dataBindings: Array<any> = [];
    orderTypes.forEach(ot => {
      dataBindings.push({
        queryString: `subject=${currentPatient.link}&category=${ot}`,
        targetResource: 'DiagnosticOrder',
        identifierType: 'array',
        identifier: ot
      });
    });
    return dataBindings;
  }

  /**
   * @method getAllOrderDetailsAndStore
   * @param orders
   * @param loader
   */
  getAllOrderDetailsAndStore(orders: IOrder[] = [], loader: boolean = true) {
    if (loader) {
      this.setLoader(true);
    }
    const orderTypes = uniq(orders.map(o => get(o, 'category.code')));
    const dataBindings = this.buildOrderDetailDataBinding(orderTypes);
    return new Promise(resolve => {
      this._bundleCall.searchCall({ queries: dataBindings }).subscribe(data => {
        const allOrderDetails = this._aggregatorService.responeRestructure(data, dataBindings);
        for (const ot of orderTypes) {
          let orderDetails = allOrderDetails.find(od => has(od, ot));
          orderDetails = get(orderDetails, `[${ot}]`, []);
          if (isEmpty(orderDetails)) {
            orderDetails = [];
          }
          orderDetails = this.removeUnwantedDataFromOrderDetails(orderDetails);
          this.updateOrderDetailsStore(orderDetails, ot);
        }
        resolve(allOrderDetails);
        this.setLoader(false);
      }, () => {
        this.setLoader(false);
      });
    });
  }

  /**
   * @method getAllOrders
   * @description Returns observable of all orders from the Store
   */
  getAllOrders(): Observable<immutableMap<string, IOrder>> {
    return this.store$.select('ordersData');
  }

  /**
   * @method getAllOrderDetails
   * @description Returns observable of all order details from the Store
   */
  getAllOrderDetails(): Observable<immutableMap<string, IOrderDetail[]>> {
    return this.store$.select('orderDetailsData');
  }

  /**
   * @description Returns observable of the selected order definition data from the Store
   * @default if match is not found, then returns the defult value
   * @param orderCategory : type of order like CT, PET-CT etc.
   */
  getOrderDefinition(orderCategory: string): Observable<ISelectedOrderDefinition> {
    return this.store$.select('ordersDefinition').pipe(
      map((orderDefinition: any) => {
        const def = get(orderDefinition, `orderDetailConfig.${orderCategory}`);
        return (def) ? def : get(orderDefinition, `orderDetailConfig.default`);
      })
    );
  }

  /**
   * @description Returns observable of all order definition data from the Store
   */
  getAllOrderDefinition(): Observable<IOrderDetailDefinition> {
    return this.store$.select('ordersDefinition');
  }

  /**
   *
   * @method setSelectedOrder
   * @description Set selected order so that it's content can be viewed in content panel,
   * @param order : order to be selected
   */
  setSelectedOrder(order: IOrder) {
    if (order && order.id) {
      this.setSelectedOrderDetails(order);
      this.store$.dispatch({
        type: ORDER_ACTIONS.CHANGE_SELECTED_ORDER,
        payload: order
      });
    }
  }

  /**
   * @method getSelectedOrder
   * @description Returns observable of selected order from Store
   */
  getSelectedOrder(): Observable<IOrder> {
    return this.store$.select('selectedOrder');
  }

  /**
   * @method getSelectedOrderDetails
   * @description Returns observable of order details of the currently selected order
   */
  getSelectedOrderDetails(): Observable<Array<IOrderDetail>> {
    return this.store$.select('selectedOrderDetails');
  }

  /**
   * @method setSelectedOrderDetails
   * @description change the selected order details based on the order selection
   * @param order
   */
  setSelectedOrderDetails(order: IOrder) {
    this.getAllOrderDetails().distinctUntilChanged().subscribe(allOrderDetails => {
      let specificOrderDetails: IOrderDetail[] = allOrderDetails.get(get(order, 'category.code')) || [];
      specificOrderDetails = specificOrderDetails
        .filter((od: IOrderDetail) => (!isEmpty(order.isActive) && order.isActive.includes(od.id))
          || ((!isEmpty(order.isActive) || isEmpty(order.isActive)) && !od.selected)
        );
      this.store$.dispatch({
        type: ORDER_ACTIONS.CHANGE_SELECTED_ORDER_DETAILS,
        payload: specificOrderDetails || []
      });
    });
  }

  /**
   * @description Updates the order (soft deletion) by turning the below flag
   * @property isActive => true
   * @param id : id of the Order
   * @param order : order to be deleted
   * @param url : endpoint of the API
   */
  deleteOrder(id: string, order: IOrder, url: string = 'order'): Observable<IOrder> {
    this.setLoader(true);
    const req = this._ispmHttpClient.put(url, order).map(newOrder => {
      this.store$.dispatch({
        type: ORDER_ACTIONS.DELETE_ORDER,
        payload: {
          id,
          data: newOrder
        }
      });
      this.setLoader(false);
      return newOrder;
    },
      () => {
        this.setLoader(false);
      }).publish();
    req.connect();
    return req;
  }

  /**
   * @description Creates new order based on the payload provided
   * @param order : order to be created
   * @param url : endpoint of the API
   * @param excludeTypes : created order type, if it exist here will not be added to store
   */
  createOrder(order: IOrder, url: string = 'order', excludeTypes = []): Observable<IOrder> {
    this.setLoader(true);
    const tempContext = cloneDeep(get(order, 'context'));
    this._contextProviderService.addContext(order, 'orderInformation');
    if (get(order, 'context')) {
      order['context'].push(...tempContext);
    } else {
      order['context'] = tempContext;
    }
    const req = this._ispmHttpClient.post(url, order).map((newOrder: IOrder) => {
      this.setLoader(false);
      if (!excludeTypes.includes(newOrder.category.code)) {
        this.updateOrderStore(newOrder);
      }
      return newOrder;
    }, () => {
      this.setLoader(false);
    }).publish();
    req.connect();
    return req;
  }

  /**
   * @description Creates new order details based on the payload provided
   * @param orderDetails
   * @param url : endpoint of the API
   */
  createOrderDetails(orderDetails: IOrderDetail[], url: string = 'bundleCreateOrders'): Observable<any> {
    return this._isgHttpClient.post('bundleCreateOrders', orderDetails).pipe(take(1));
  }

  /**
   * @description Updates order details based on the payload provided
   * @param data
   * @param url : endpoint of the API
   */
  updateOrderDetails(data: IOrderDetail[], url: string = 'orderdetails'): Observable<IOrderDetail[]> {
    return this._ispmHttpClient.put(url, data).pipe(take(1));
  }

  /**
   * @description Updates order based on the payload provided
   * @param data
   * @param url : endpoint of the API
   */
  updateOrder(data: IOrder, url: string = 'order'): Observable<IOrder> {
    return this._ispmHttpClient.put(url, data).pipe(take(1));
  }

  /**
   * @description method to update the order details in store
   * @param orderDetails - collection of order details of same category
   * @param category - eg. CT, PET-CT, MRI etc
   */
  updateOrderDetailsStore(orderDetails: IOrderDetail[] = [], category: string) {
    this.store$.dispatch({
      type: ORDER_ACTIONS.UPDATE_ORDER_DETAILS,
      payload: {
        id: category,
        data: sortBy(
          orderDetails.filter(od => od.isDeleted !== true),
          o => new Date(get(o, 'meta.created', null))
        )
      }
    });
  }

  /**
   * @method updateOrderStore
   * @description method to update the orders in store
   * @param order
   */
  updateOrderStore(order: IOrder) {
    this.store$.dispatch({
      type: ORDER_ACTIONS.UPDATE_ORDER,
      payload: {
        id: order.id,
        data: order
      }
    });
  }
  /**
   * @method setLoader
   * @description method to show / hide the core loader
   * @param flag : boolean flag to turn on/off the loader
   */
  setLoader(flag: boolean) {
    this.store.dispatch({
      type: CORE_ACTIONS.SET_LOADER,
      payload: flag
    });
  }

  /**
   * @method resetStore
   * @description Resets the order from the store that includes order, order details, selected order etc.
   */
  resetStore() {
    this.store$.dispatch({
      type: ORDER_ACTIONS.CLEAR_ORDERS,
      payload: null
    });
  }

  /**
   * check if context is there
   * @param order
   */
  checkOrderHasContext(order): boolean {
    const orderContexts = this._contextProviderService.getFormattedContext('orderInformation');
    return orderContexts.every(context => {
      let hasContext = false;
      order['context'].forEach(tempContext => {
        if (tempContext['reference'] === context['reference']) {
          hasContext = true;
        }
      });
      return hasContext;
    });
  }

  /**
   * @description Returns Observable of diagnosticOrder array associated with OrderInformation
   * @param orders : Array of OrderInformation
   */
  getAllBiodesixOrderDetails(orders: Array<IOrder>): Observable<any> {
    const dataBindingsList = [];
      orders.forEach( order => {
        if (!isEmpty(order.isActive[0])) {
          dataBindingsList.push({
            'identifierType': 'array',
            'identifier': 'OrdersDO',
            'queryString': `_id=${order.isActive[0]}`,
            'targetResource': 'DiagnosticOrder'

          });
        }
    });
    return this._bundleCall.searchCall({ queries: dataBindingsList });
  }

  // Please check with Sudarshan for better access of the acr option payload instead of redux store
  // Also optimize this code in better way
  triggerAcrValidation(payload: { orderId: string, eventId: string }, showLoader: boolean = false) {
    const resourceType: any = 'generic';
    const templates$ = this.getTablelistTemplateSnapshot();
    templates$.pipe(
      take(1))
      .subscribe((templates: any ) => {
      if (!isEmpty(templates)) {
       const template: any = templates['lungACR'];
       this.processACRValidation(template.config, payload, showLoader);

      } else {
        this.tablelistService.loadConfigTemplate(resourceType, 'lungACR').pipe(take(1)).subscribe((worklistTemplate) => {
            const worklistTemplateStore = {
              configTemplateType: get(worklistTemplate, 'type'),
              item: worklistTemplate,
              resourceType: get(worklistTemplate, 'resourceTypeCode', 'generic')
            };
            this._tablelistStoreHandlerService.addWorklistTempleteToStore(worklistTemplateStore);
            this.processACRValidation(worklistTemplateStore.item, payload, showLoader) ;
          }
        );

      }
    });
  }

  processACRValidation(item: TablelistStateListItemConfig, payload: { orderId: string, eventId: string }, showLoader: boolean) {
    const exportOption: Partial<ExportConfig> = item.exportConfiguration.options.filter(item => item.label === 'Export File')[0];
    if ( exportOption ) {
      const combinedPayload = Object.assign({ }, { generatePayload : exportOption.ApiResource.payloadData}, payload );
      delete combinedPayload['generatePayload']['diagnosticOrderIdArray'];
      this._workflowUtilService.triggerACRValidation(combinedPayload, false);
    }
  }

  setLastEventProcessed(lastEventProcessed: IEvent) {
    this.lastEventProcessed = lastEventProcessed;
  }

  getLastEventProcessed(): IEvent {
    return this.lastEventProcessed;
  }

  getDiagnosticOrderFromStore(): Promise<IDiagnosticOrder> {
    return this.appStore$.select(currentDiagnosticOrderSelector).pipe(distinctUntilChanged(), take(1)).toPromise();
  }

  updateDiagnosticOrder(payload: any): Observable<IDiagnosticOrder> {
    return this._isgHttpClient.put('api/v2/bundle', payload)
      .pipe(take(1), map(body => get(body, 'body[0]')), catchError(err =>
        {
        console.error('Order service: Diagonostic Order Update Failed : Error : ',err.status,'-', err.message);
         return of({
        isError: true,
        error: err
      })}));
  }

  updateWorkflowRequest(payload: any): Observable<any> {
    return this._ispmHttpClient.post('workflow/action', payload)
      .pipe(take(1), catchError(err => {
        console.error('Order service: Workflow request Update Failed : Error : ',err.status,'-', err.message);
       return of({
      isError: true,
      error: err
    })}));
  }

  updateCurrentDiagnosticOrderInStore(payload: any): void {
    this.appStore$.dispatch({
      type: DIAGNOSTIC_ORDER_ACTIONS.SET_DIAGNOSTIC_ORDER_CONFIG,
      payload: payload
    });
  }

  updateWorkflowActionResourceInStore(payload: any): void {
    this.store$.dispatch(new UpdateResourcesAction([new ResourceDataItem(payload, 'WorkflowRequest')]));
  }

  getActiveWorkflowRequestFromStore(): Promise<IWorkflowRequest>  {
    return this.appStore$.select(selectActiveWorkflow).pipe(distinctUntilChanged(), take(1)).toPromise();
  }

  removeFallbackKeysFromForm(data: any) {
    const keys = [
      'fallbackDob',
      'fallbackMrn',
      'fallbackEmail',
      'fallbackCountry',
      'fallbackPgender',
      'fallbackMobileNo',
      'fallbackPostalCode',
      'fallbackAddressCity',
      'fallbackAddressState',
      'fallbackAddressStreet',
      'fallbackPatientLastName',
      'fallbackPatientMiddleName',
      'fallbackPatientFirstName',
      'fallbackPractitionerEmail',
      'fallbackPractitionerLastName',
      'fallbackPractitionerMiddleName',
      'fallbackPractitionerMobileNo',
      'fallbackPractitionerNPI',
      'fallbackPractitionerFirstName'
    ];

    return this.removeKeys(data, keys);
  }

  removeKeys = (obj: any, keys: string[]) => obj !== Object(obj)
      ? obj
      : Array.isArray(obj)
        ? obj.map((item) => this.removeKeys(item, keys))
        : Object.keys(obj)
          .filter((val) => !keys.includes(val))
          .reduce(
            (acc, cur) => Object.assign(acc, { [cur]: this.removeKeys(obj[cur], keys) }),
            { }
          )

  validateWorkflowRequestId = (orderDetails: any) => {
    if (orderDetails.length === 1 && orderDetails[0].workflowRequest && typeof orderDetails[0].workflowRequest === 'object' && orderDetails[0].identifiers && orderDetails[0].identifiers.length > 0 &&
      orderDetails[0].identifiers[0].value && orderDetails[0].identifiers[0].value.startsWith('LABTEST-')) {
      orderDetails[0].workflowRequest = orderDetails[0].workflowRequest.id;
      return orderDetails;
    }
    return orderDetails;
  }

  removeUnwantedDataFromOrderDetails(orderDetails: IOrderDetail[]): IOrderDetail[] {
    return orderDetails.map(orderDetail => {
      unset(orderDetail, 'testConfigurations');
      unset(orderDetail, 'client_defined_identifier');
      unset(orderDetail, 'apiGenomicOrderAssignees');
      unset(orderDetail, 'managingOrganization');
      unset(orderDetail, 'apiGenomicOrderPatients');
      return orderDetail;
    });
  }

  getTablelistTemplateSnapshot(): Observable<TablelistStateListItemConfig> {
    return this.store.select('tablelist', 'list');
  }
}
